import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { AgentMessageEventLlmCallData, AgentMessageEventOutput, GuardrailType } from '../../../../gamma-api';
import IbCollapse, { IbCollapsePanel } from '../../common/IbCollapse';
import IbIcon, { IbIconName } from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import MarkdownContent from '../../MarkdownContent';
import { isEmptyValue } from '../../../utils/valueUtil';
import { agentMessageEventDataToMarkdown } from '../utils';
import {
  getGuardrailCheckStatus,
  getGuardrailCheckedTargets,
  getGuardrailCheckTypeTitle,
  GuardrailCheckData,
  GuardrailCheckedTarget,
  GuardrailCheckedTargetType,
  GuardrailCheckedType,
  isGuardrailCheckBlocked,
  isGuardrailCheckFailed,
  isGuardrailCheckWarning,
} from '../guardrailChecks';
import {
  buildGuardrailFlow,
  getGuardrailDetailsPanelKey,
  GuardrailDetailsSection,
  GuardrailFlowItemType,
  GuardrailDetailsTarget,
  GuardrailLlmGroup,
} from '../guardrailEventGroups';

const MAIN_CLASS_NAME = 'guardrail-checks-event-details';
const GROUP_CLASS_NAME = `${MAIN_CLASS_NAME}__group`;
const PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__panel`;
const PANEL_HEADER_CLASS_NAME = `${MAIN_CLASS_NAME}__panel-header`;
const LLM_CALL_PANEL_CLASS_NAME = `${PANEL_CLASS_NAME}_llm`;
const LLM_CALL_PANEL_CLASSES = [PANEL_CLASS_NAME, LLM_CALL_PANEL_CLASS_NAME].join(' ');
const CHECK_CLASS_NAME = `${MAIN_CLASS_NAME}__check`;
const CHECK_HEADER_CLASS_NAME = `${CHECK_CLASS_NAME}__header`;
const CHECK_TYPE_CLASS_NAME = `${CHECK_CLASS_NAME}__type`;
const STATUS_CLASS_NAME = `${MAIN_CLASS_NAME}__status`;
const STATUS_ERROR_CLASS_NAME = `${STATUS_CLASS_NAME}_error`;
const STATUS_WARNING_CLASS_NAME = `${STATUS_CLASS_NAME}_warning`;
const DETAILS_TABLE_CLASS_NAME = `${MAIN_CLASS_NAME}__details-table`;
const DETAILS_TABLE_VALUE_CLASS_NAME = `${DETAILS_TABLE_CLASS_NAME}__value`;
const CHECKED_LIST_CLASS_NAME = `${MAIN_CLASS_NAME}__checked-list`;
const CHECKED_ITEM_CLASS_NAME = `${MAIN_CLASS_NAME}__checked-item`;
const TOOL_NAME_CLASS_NAME = `${MAIN_CLASS_NAME}__tool-name`;

const TRANSLATE_KEY = 'Agent: Guardrail checks event details:';

const GUARDRAIL_CHECK_RESULT_DETAIL_KEYS = {
  stage: 'stage',
  checkResult: 'check_result',
  confidence: 'confidence',
  reason: 'reason',
  errorMessage: 'error_message',
  provider: 'provider',
  modelName: 'model_name',
  checkedContent: 'checked_content',
} as const;

type GuardrailCheckDetailKey = typeof GUARDRAIL_CHECK_RESULT_DETAIL_KEYS[keyof typeof GUARDRAIL_CHECK_RESULT_DETAIL_KEYS];

const DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS: GuardrailCheckDetailKey[] = [
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkedContent,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage,
];

const GUARDRAIL_CHECK_DETAIL_KEYS_BY_TYPE: Partial<Record<GuardrailType, GuardrailCheckDetailKey[]>> = {
  [GuardrailType.CustomPromptCheck]: [
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.provider,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.modelName,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkedContent,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage,
  ],
  [GuardrailType.ExternalCheck]: DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS,
  [GuardrailType.RegexCheck]: DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS.filter(
    (key) => key !== GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence
  ),
};

export interface IGuardrailChecksEventDetailsProps {
  events: AgentMessageEventOutput[];
  navigationTarget?: GuardrailDetailsTarget;
}

interface IGuardrailCheckDetail {
  key: GuardrailCheckDetailKey;
  value: unknown;
}

const getProviderLabel = (provider?: { name?: string | null; type?: string | null } | null): string | undefined => {
  if (!provider?.name) {
    return undefined;
  }
  return provider.type ? `${provider.name} (${provider.type})` : provider.name;
};

const getGuardrailCheckDetailValue = (data: GuardrailCheckData, key: GuardrailCheckDetailKey): unknown => {
  switch (key) {
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage:
      return data.stage;
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult:
      return isGuardrailCheckFailed(data) && isGuardrailCheckBlocked(data)
        ? 'BlockedError'
        : getGuardrailCheckStatus(data);
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence:
      return data.confidence;
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason:
      return data.reason?.trim();
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage:
      return data.error_message?.trim();
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.provider:
      return getProviderLabel(data.provider);
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.modelName:
      return data.model_name?.trim();
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkedContent:
      return getGuardrailCheckedTargets(data);
    default:
      return undefined;
  }
};

const buildGuardrailCheckDetails = (data: GuardrailCheckData): IGuardrailCheckDetail[] => {
  const detailKeys = GUARDRAIL_CHECK_DETAIL_KEYS_BY_TYPE[data.guardrail_type] || DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS;
  return detailKeys
    .map((key) => ({ key, value: getGuardrailCheckDetailValue(data, key) }))
    .filter(({ value }) => !isEmptyValue(value));
};

const getCheckedItemIcon = (type: GuardrailCheckedTargetType): IbIconName => {
  switch (type) {
    case GuardrailCheckedType.UserMessage:
      return 'me';
    case GuardrailCheckedType.AgentMessage:
      return 'robot-one';
    case GuardrailCheckedType.ToolCall:
      return 'tool';
    case GuardrailCheckedType.ToolResult:
      return 'terminal';
    case GuardrailCheckedType.History:
      return 'comments';
    default:
      return 'comment';
  }
};

const getCheckTargetKey = (panelKey: string, checkIndex: number): string => `${panelKey}:check:${checkIndex}`;

const getDefaultActivePanelKeys = (group: GuardrailLlmGroup): string[] => {
  const keys: string[] = [];
  if (group.inputChecks.length) {
    keys.push(getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Input));
  }
  if (group.llmCall) {
    keys.push(getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Llm));
  }
  if (group.outputChecks.length) {
    keys.push(getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Output));
  }
  return keys;
};

const GuardrailChecksEventDetails: React.FC<IGuardrailChecksEventDetailsProps> = ({ events, navigationTarget }) => {
  const { t } = useTranslation();
  const groups = useMemo(
    () =>
      buildGuardrailFlow(events)
        .filter(
          (item): item is { type: GuardrailFlowItemType.Llm; group: GuardrailLlmGroup } =>
            item.type === GuardrailFlowItemType.Llm
        )
        .map((item) => item.group)
        .filter((group) => group.llmCall || group.inputChecks.length || group.outputChecks.length),
    [events]
  );
  const [activePanelKeys, setActivePanelKeys] = useState<Record<string, string[]>>(() =>
    groups.reduce<Record<string, string[]>>(
      (keys, group) => ({ ...keys, [group.key]: getDefaultActivePanelKeys(group) }),
      {}
    )
  );
  const navigationTargetRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const translateDetail = (key: string) => t(`${TRANSLATE_KEY} ${key}`);

  useEffect(() => {
    setActivePanelKeys(
      groups.reduce<Record<string, string[]>>(
        (keys, group) => ({ ...keys, [group.key]: getDefaultActivePanelKeys(group) }),
        {}
      )
    );
  }, [groups]);

  useEffect(() => {
    if (!navigationTarget) {
      return;
    }
    const panelKey = getGuardrailDetailsPanelKey(navigationTarget.groupKey, navigationTarget.section);
    setActivePanelKeys((keys) =>
      keys[navigationTarget.groupKey]?.includes(panelKey)
        ? keys
        : {
            ...keys,
            [navigationTarget.groupKey]: [...(keys[navigationTarget.groupKey] || []), panelKey],
          }
    );
  }, [navigationTarget]);

  useEffect(() => {
    if (!navigationTarget) {
      return;
    }
    const panelKey = getGuardrailDetailsPanelKey(navigationTarget.groupKey, navigationTarget.section);
    if (!activePanelKeys[navigationTarget.groupKey]?.includes(panelKey)) {
      return;
    }
    const targetKey =
      navigationTarget.checkIndex === undefined ? panelKey : getCheckTargetKey(panelKey, navigationTarget.checkIndex);
    navigationTargetRefs.current[targetKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activePanelKeys, navigationTarget]);

  const getStatusClasses = (check: GuardrailCheckData): string => {
    return [
      STATUS_CLASS_NAME,
      ...(isGuardrailCheckBlocked(check) ? [STATUS_ERROR_CLASS_NAME] : []),
      ...(isGuardrailCheckWarning(check) ? [STATUS_WARNING_CLASS_NAME] : []),
    ].join(' ');
  };

  const renderCheckedTargets = (targets: GuardrailCheckedTarget[]) => (
    <div className={CHECKED_LIST_CLASS_NAME}>
      {targets.map((item, index) => (
        <div key={`${item.type}:${item.toolName || ''}:${index}`} className={CHECKED_ITEM_CLASS_NAME}>
          <IbIcon iconName={getCheckedItemIcon(item.type)} size={16} />
          <IbTypography.Paragraph type="secondary">
            {item.type === GuardrailCheckedType.History
              ? translateDetail('Checked item: history')
              : translateDetail(`Checked item: ${item.type}`)}
            {item.toolName && <strong className={TOOL_NAME_CLASS_NAME}> {item.toolName}</strong>}
          </IbTypography.Paragraph>
        </div>
      ))}
    </div>
  );

  const renderDetailValue = (key: GuardrailCheckDetailKey, value: unknown) => {
    if (key === GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkedContent) {
      return renderCheckedTargets(value as GuardrailCheckedTarget[]);
    }
    if (key === GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult) {
      return translateDetail(`Check result: ${value}`);
    }
    if (key === GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage) {
      return translateDetail(`Stage: ${value}`);
    }
    if (typeof value === 'object') {
      return <MarkdownContent>{agentMessageEventDataToMarkdown(value)}</MarkdownContent>;
    }
    return String(value);
  };

  const renderCheck = (check: GuardrailCheckData, index: number, panelKey: string) => {
    const isBlocked = isGuardrailCheckBlocked(check);
    return (
      <div
        key={`${check.check_id}:${index}`}
        ref={(element) => {
          navigationTargetRefs.current[getCheckTargetKey(panelKey, index)] = element;
        }}
        className={CHECK_CLASS_NAME}
      >
        <div className={CHECK_HEADER_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">
            {check.check_name?.trim() || translateDetail('Unnamed check')}{' '}
            <span className={CHECK_TYPE_CLASS_NAME}>({getGuardrailCheckTypeTitle(check)})</span>
          </IbTypography.Paragraph>
          <IbIcon
            className={getStatusClasses(check)}
            iconName={isBlocked ? 'close-small' : isGuardrailCheckWarning(check) ? 'attention' : 'check-small'}
            size={16}
          />
        </div>
        <div className={DETAILS_TABLE_CLASS_NAME}>
          {buildGuardrailCheckDetails(check).map(({ key, value }) => (
            <React.Fragment key={key}>
              <IbTypography.Paragraph disabled type="secondary">
                {translateDetail(key)}
              </IbTypography.Paragraph>
              <div className={DETAILS_TABLE_VALUE_CLASS_NAME}>{renderDetailValue(key, value)}</div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderPanelHeader = (iconName: IbIconName, title: string, panelKey: string) => (
    <div
      ref={(element) => {
        navigationTargetRefs.current[panelKey] = element;
      }}
      className={PANEL_HEADER_CLASS_NAME}
    >
      <IbIcon iconName={iconName} size={16} />
      <IbTypography.Paragraph strong type="secondary">
        {title}
      </IbTypography.Paragraph>
    </div>
  );

  const renderLlmDetails = (llmCall: AgentMessageEventLlmCallData) => {
    const rows = [
      ['Provider', getProviderLabel(llmCall.provider)],
      ['Model', llmCall.model_name],
    ].filter(([, value]) => !isEmptyValue(value));

    return (
      <div className={DETAILS_TABLE_CLASS_NAME}>
        {rows.map(([label, value]) => (
          <React.Fragment key={String(label)}>
            <IbTypography.Paragraph disabled type="secondary">
              {t(`Agent: LLM call event details: ${label}`)}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{value}</IbTypography.Paragraph>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderGroup = (group: GuardrailLlmGroup) => {
    const inputKey = getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Input);
    const llmKey = getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Llm);
    const outputKey = getGuardrailDetailsPanelKey(group.key, GuardrailDetailsSection.Output);

    return (
      <IbCollapse
        key={group.key}
        accordion={false}
        activeKey={activePanelKeys[group.key]}
        className={GROUP_CLASS_NAME}
        expandIcon={({ isActive }) => <IbIcon iconName={isActive ? 'down' : 'right'} size={20} />}
        expandIconPosition="right"
        onChange={(key) =>
          setActivePanelKeys((keys) => ({
            ...keys,
            [group.key]: Array.isArray(key) ? key : key ? [key] : [],
          }))
        }
      >
        {!!group.inputChecks.length && (
          <IbCollapsePanel
            key={inputKey}
            className={PANEL_CLASS_NAME}
            header={renderPanelHeader('guardrail-input', t('Agent: Guardrail checks timeline: Input checks'), inputKey)}
          >
            {group.inputChecks.map((check, index) => renderCheck(check, index, inputKey))}
          </IbCollapsePanel>
        )}
        {group.llmCall && (
          <IbCollapsePanel
            key={llmKey}
            className={LLM_CALL_PANEL_CLASSES}
            header={renderPanelHeader('robot-one', `${t('Agent: Guardrail checks timeline: Agent LLM call')}`, llmKey)}
          >
            {renderLlmDetails(group.llmCall)}
          </IbCollapsePanel>
        )}
        {!!group.outputChecks.length && (
          <IbCollapsePanel
            key={outputKey}
            className={PANEL_CLASS_NAME}
            header={renderPanelHeader(
              'guardrail-output',
              t('Agent: Guardrail checks timeline: Output checks'),
              outputKey
            )}
          >
            {group.outputChecks.map((check, index) => renderCheck(check, index, outputKey))}
          </IbCollapsePanel>
        )}
      </IbCollapse>
    );
  };

  return <div className={MAIN_CLASS_NAME}>{groups.map(renderGroup)}</div>;
};

export default GuardrailChecksEventDetails;
